import React, { Component } from 'react';
import { Text, View, Image, Switch, StyleSheet } from 'react-native';

import { __WEB__, COLOR, FONT, FONT_SIZE, THEME } from '../../constants/styles';
import { convertDateFromTZ, convertDateToTZ } from './timezone_helper';
import { defaultEndDateFromStart } from './event_UI_helpers';

import moment from 'moment';

import { I18n, OPComponent, IconButton, Utils, Navigation, DatePicker } from '../components';

export default class EventDateTime extends OPComponent {
  constructor(props) {
    super(props);

    let tz = props.timeZone || 'America/Los_Angeles';

    let convertedStart = props.startDate && convertDateFromTZ(props.startDate, tz);
    let convertedEnd = props.endDate && convertDateFromTZ(props.endDate, tz);

    let startDate = new Date(convertedStart || this.defaultStartDate());

    let minEndDate = this.minEndDateFromStart(startDate);

    const dateForDefaultEndTime = props.dateForDefaultEndTime
      ? new Date(props.dateForDefaultEndTime)
      : null;

    let endDate = convertedEnd || defaultEndDateFromStart(startDate, dateForDefaultEndTime);

    this._endHasBeenSet = false;

    this.state = {
      allDay: props.allDay || false,
      start: startDate,
      minEndDate: minEndDate,
      end: endDate,
      timeZone: tz,
      dateForDefaultEndTime,
    };

    // make sure we notify of the default values in case the user doesn't select
    // anything in the UI
    this.props.onStartDate(convertDateToTZ(this.state.start, tz));
    this.props.onEndDate(convertDateToTZ(this.state.end, tz));
    this.props.onAllDay(this.state.allDay);
    this.props.onTimeZone && this.props.onTimeZone(this.state.timeZone);
  }

  defaultStartDate() {
    let newDate = new Date();
    newDate.setMinutes(0);

    return newDate;
  }

  minEndDateFromStart(startDate) {
    if (this.state && this.state.allDay) return moment(startDate).endOf('day').toDate();

    return new Date(startDate.getTime() + 60 * 15 * 1000);
  }

  setStartDate = (newStart) => {
    Utils.trackEvent('Action', 'Set', 'Event Start Date');
    let { timeZone } = this.state;

    let minEndDate = this.minEndDateFromStart(newStart);
    let defaultEndDate = defaultEndDateFromStart(newStart, this.state.dateForDefaultEndTime);

    let newEnd = this._endHasBeenSet ? this.state.end : defaultEndDate;
    if (minEndDate > newEnd) newEnd = minEndDate;

    this.setState(
      {
        start: newStart,
        minEndDate: minEndDate,
      },
      () => {
        //have to do this after minEndDate has been updated because it
        // seems to hold to the previous value when they are set at the same time
        this.setState({ end: newEnd });
        this.props.onStartDate(convertDateToTZ(newStart, timeZone));
        this.props.onEndDate(convertDateToTZ(newEnd, timeZone));
      }
    );
  };

  setEndDate = (newEnd) => {
    Utils.trackEvent('Action', 'Set', 'Event Finish Date');
    this._endHasBeenSet = true;

    this.setState({
      end: newEnd,
    });

    this.props.onEndDate(convertDateToTZ(newEnd, this.state.timeZone));
  };

  toggleAllDay = (value) => {
    Utils.trackEvent('Action', 'Set', 'Toggle All Day', parseFloat(value));
    // set this on state here cause its required by setStartDate and we're not waiting
    // for the render loop before calling it.
    this.state.allDay = value;
    this.setState({ allDay: value });
    this.props.onAllDay(value);

    if (value) {
      const startDate = moment(this.state.start).startOf('day').toDate();
      // endHasBeenSet equal false indicates to setStartDate to auto generate an end date
      this._endHasBeenSet = false;
      this.setStartDate(startDate);
      this._endHasBeenSet = false;
    } else {
      const nowDate = new Date();
      const newDate = this.state.start;
      newDate.setHours(nowDate.getHours());
      newDate.setMinutes(0);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      this._endHasBeenSet = false;
      this.setStartDate(newDate);
    }
  };

  goToTimeZoneSelect = () => {
    Utils.trackEvent('Navigation', 'Load', 'TimeZone Sheet');
    Navigation.push(
      {
        screen: 'EventTimeZones',
        title: I18n.t('calendar.timeZone'),
        passProps: {
          setSelected: (timeZone) => {
            this.setState({ timeZone });
            this.props.onTimeZone(timeZone);
            this.props.onStartDate(convertDateToTZ(this.state.start, timeZone));
            this.props.onEndDate(convertDateToTZ(this.state.end, timeZone));
          },
          selectedId: null,
        },
      },
      this.props.componentId
    );
  };

  render() {
    const timeOnly = this.props.mode === 'time';
    return (
      <View style={[__WEB__ ? THEME.container : THEME.containerAlt, this.props.containerStyle]}>
        {!this.props.hideTimeZone && (
          <View style={{ backgroundColor: COLOR.contrast }}>
            <IconButton
              label={I18n.t('calendar.timeZone')}
              value={this.state.timeZone}
              imageSource={__WEB__ ? null : require('../../../resources/icon-time.png')}
              noIcon={!!__WEB__}
              onPress={this.goToTimeZoneSelect}
              containerStyle={styles.timezoneButtonContainer}
              labelStyle={styles.timezoneLabelStyle}
            />
          </View>
        )}
        <View style={styles.allDayRow}>
          {!__WEB__ && (
            <Image
              style={styles.settingIcon}
              source={require('../../../resources/icon-time.png')}
            />
          )}
          <Text style={styles.settingText}>{I18n.t('calendar.allDay')}</Text>
          <Switch value={this.state.allDay} onValueChange={this.toggleAllDay} />
        </View>

        {!(timeOnly && this.state.allDay) && (
          <View
            style={{
              zIndex: this.state.active === 'startDate' ? 1000 : 0,
              overflow: 'visible',
            }}
          >
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerHeaderText}>{I18n.t('calendar.start')}</Text>
            </View>
            <View style={styles.datePickerContainer}>
              <DatePicker
                selectedDate={this.state.start}
                onDate={this.setStartDate}
                mode={timeOnly ? 'time' : this.state.allDay ? 'date' : 'datetime'}
                onFocus={() => this.setState({ active: 'startDate' })}
                onBlur={() => this.setState({ active: '' })}
              />
            </View>
          </View>
        )}

        {!this.state.allDay && (
          <View
            style={{
              zIndex: this.state.active === 'endDate' ? 1000 : 0,
              overflow: 'visible',
            }}
          >
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerHeaderText}>{I18n.t('calendar.end')}</Text>
            </View>
            <View style={styles.datePickerContainer}>
              <DatePicker
                selectedDate={this.state.end}
                onDate={this.setEndDate}
                minimumDate={this.state.minEndDate}
                mode={timeOnly ? 'time' : 'datetime'}
                onFocus={() => this.setState({ active: 'endDate' })}
                onBlur={() => this.setState({ active: '' })}
              />
            </View>
          </View>
        )}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  scrollViewContent: {
    paddingTop: 40,
  },
  allDayRow: {
    borderColor: COLOR.accent,
    borderBottomWidth: 1,
    paddingVertical: 15,
    // height: 50,
    backgroundColor: __WEB__ ? 'transparent' : COLOR.contrast,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: __WEB__ ? 0 : 25,
    paddingRight: 25,
  },
  timezoneButtonContainer: {
    paddingHorizontal: __WEB__ ? 0 : 25,
  },
  timezoneLabelStyle: {
    marginLeft: __WEB__ ? 0 : 20,
  },
  settingIcon: {
    width: 20,
    height: 20,
  },
  settingText: {
    flex: __WEB__ ? null : 1,
    ...FONT.regular,
    fontSize: FONT_SIZE.large,
    color: COLOR.textLight,
    marginLeft: __WEB__ ? 0 : 20,
    marginRight: __WEB__ ? 20 : 0,
  },
  pickerHeader: {
    height: 60,
    justifyContent: 'center',
    paddingHorizontal: __WEB__ ? 0 : 25,
    borderColor: COLOR.accent,
    borderBottomWidth: 2,
  },
  pickerHeaderText: {
    ...FONT.medium,
    fontSize: FONT_SIZE.xlarge,
    color: COLOR.text,
  },
  datePickerContainer: {
    backgroundColor: COLOR.contrast,
  },
});
